import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { REGIONI, SETTORI, TIPI_PIVA, DIMENSIONI, CASSE } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { clearToken } from "@/lib/tokenStorage";
import { toast } from "sonner";
import {
  Save, User as UserIcon, MapPin, Briefcase, AlertCircle, Download, Trash2, Shield, Bell, Send,
  Phone, Landmark, Award, Wallet, CheckCircle2, Sparkles, Loader2, X, CalendarDays, RefreshCw
} from "lucide-react";

const EMPTY_FORM = {
  // Personali
  full_name: "", codice_fiscale: "", data_nascita: "", luogo_nascita: "",
  // Contatti
  telefono: "", pec: "", sito_web: "",
  // Business
  ragione_sociale: "", partita_iva: "", tipo_piva: "nessuna",
  codice_ateco: "", codici_ateco_secondari: "", settore: "",
  data_inizio_attivita: "", fatturato_ultimo_anno: "", dimensione: "solo",
  // Sede
  indirizzo: "", cap: "", citta: "", provincia: "", regione: "",
  // CCIAA & Albi
  rea_numero: "", rea_cciaa: "", rea_data_iscrizione: "",
  albo_nome: "", albo_numero: "", albo_data_iscrizione: "",
  // Casse
  cassa_previdenziale: "", cassa_numero: "", inps_matricola: "", inail_pat: "",
  // Bancari
  iban: "", banca_nome: "", codice_sdi: "", pec_fatturazione: "",
  // Preferences
  email_notifications: true,
  phone_number: "",
  email_deadlines_alerts: true,
  email_bandi_alerts: true,
  whatsapp_alerts: false,
  // Dipendente / famiglia
  has_dipendente_income: false,
  ral_lordo: "", tipo_contratto: "indeterminato", mensilita: 13,
  aliquota_add_regionale: "", aliquota_add_comunale: "",
  figli_a_carico: [],
  coniuge_a_carico: false,
  isee: "",
  madre_under_21: false,
  entrambi_genitori_lavoratori: false,
};

/** Helper: renders label+input pair to keep JSX compact. */
function Field({ label, value, onChange, placeholder = "", hint, testid, type = "text", disabled = false, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold text-muted-foreground">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`h-11 rounded-xl mt-1 ${disabled ? "opacity-60" : ""}`}
        data-testid={testid}
      />
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

/** Header for each accordion section */
function SectionHeader({ Icon, title, subtitle, filledCount, totalCount }) {
  const done = filledCount === totalCount && totalCount > 0;
  return (
    <div className="flex items-center gap-3 w-full">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${done ? "bg-primary/10" : "bg-accent"}`}>
        {done ? <CheckCircle2 className="w-5 h-5 text-primary" /> : <Icon className="w-5 h-5 text-primary" />}
      </div>
      <div className="text-left flex-1 min-w-0">
        <div className="font-heading font-semibold text-base leading-tight">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</div>}
      </div>
      {totalCount > 0 && (
        <div className={`text-xs font-mono px-2 py-1 rounded-md ${done ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
          {filledCount}/{totalCount}
        </div>
      )}
    </div>
  );
}

const countFilled = (form, keys) => keys.filter((k) => {
  const v = form[k];
  return v !== "" && v !== null && v !== undefined && v !== false;
}).length;

const SECTIONS = {
  personal:   ["full_name", "codice_fiscale", "data_nascita", "luogo_nascita"],
  contatti:   ["telefono", "pec", "sito_web"],
  sede:       ["indirizzo", "cap", "citta", "provincia", "regione"],
  attivita:   ["ragione_sociale", "partita_iva", "tipo_piva", "codice_ateco", "settore", "data_inizio_attivita", "dimensione"],
  cciaa:      ["rea_numero", "rea_cciaa", "rea_data_iscrizione", "albo_nome", "albo_numero", "albo_data_iscrizione"],
  casse:      ["cassa_previdenziale", "cassa_numero", "inps_matricola", "inail_pat"],
  bancari:    ["iban", "banca_nome", "codice_sdi", "pec_fatturazione"],
};

export default function Settings() {
  const { user, realUser, setUser } = useAuth();
  const profileSource = realUser || user; // Admin viewMode non deve influenzare il form Settings
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    if (profileSource) {
      setForm({
        ...EMPTY_FORM,
        ...Object.fromEntries(
          Object.keys(EMPTY_FORM).map((k) => [
            k,
            profileSource[k] !== undefined && profileSource[k] !== null
              ? (k === "fatturato_ultimo_anno" ? String(profileSource[k] ?? "") : profileSource[k])
              : EMPTY_FORM[k],
          ])
        ),
      });
    }
  }, [profileSource]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Lookup coefficiente forfettario da codice ATECO (debounced)
  const [atecoInfo, setAtecoInfo] = useState(null);
  useEffect(() => {
    const code = (form.codice_ateco || "").trim();
    if (!code) { setAtecoInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/audit/ateco-coefficient?ateco=${encodeURIComponent(code)}`);
        setAtecoInfo(r.data);
      } catch { setAtecoInfo(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.codice_ateco]);

  // Aiutante AI ATECO — modal state
  const [atecoAiOpen, setAtecoAiOpen] = useState(false);
  const [atecoAiDesc, setAtecoAiDesc] = useState("");
  const [atecoAiLoading, setAtecoAiLoading] = useState(false);
  const [atecoAiResults, setAtecoAiResults] = useState([]);

  const askAtecoAI = async () => {
    if (atecoAiDesc.trim().length < 5) {
      toast.error("Descrivi la tua attività in almeno 5 caratteri");
      return;
    }
    setAtecoAiLoading(true);
    setAtecoAiResults([]);
    try {
      const r = await api.post("/audit/ateco-suggest", { descrizione: atecoAiDesc });
      setAtecoAiResults(r.data.suggestions || []);
      if ((r.data.suggestions || []).length === 0) {
        toast.info("Nessun codice trovato, prova con più dettagli");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI, riprova");
    } finally { setAtecoAiLoading(false); }
  };

  const applyAtecoSuggestion = (codice) => {
    set("codice_ateco")(codice);
    setAtecoAiOpen(false);
    setAtecoAiDesc("");
    setAtecoAiResults([]);
    toast.success(`Codice ATECO ${codice} applicato — ricordati di salvare`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // fatturato: string → number or null
      if (payload.fatturato_ultimo_anno === "" || payload.fatturato_ultimo_anno === null) {
        delete payload.fatturato_ultimo_anno;
      } else {
        payload.fatturato_ultimo_anno = parseFloat(payload.fatturato_ultimo_anno);
        if (isNaN(payload.fatturato_ultimo_anno)) delete payload.fatturato_ultimo_anno;
      }
      // Conversione campi numerici dipendente / famiglia (string vuota → null; NaN → null)
      const numFields = ["ral_lordo", "aliquota_add_regionale", "aliquota_add_comunale", "isee"];
      for (const k of numFields) {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          payload[k] = null;
        } else {
          const n = parseFloat(payload[k]);
          payload[k] = isNaN(n) ? null : n;
        }
      }
      if (payload.mensilita !== null && payload.mensilita !== undefined && payload.mensilita !== "") {
        payload.mensilita = parseInt(payload.mensilita) || null;
      }
      const r = await api.patch("/auth/profile", payload);
      setUser(r.data);
      toast.success("Profilo aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const exportData = async () => {
    try {
      const r = await api.get("/auth/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tuotax_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dati esportati");
    } catch { toast.error("Errore export"); }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteConferma, setDeleteConferma] = useState("");
  const [deleting, setDeleting] = useState(false);

  const doDeleteAccount = async () => {
    setDeleting(true);
    try {
      const r = await api.delete("/auth/account", { data: { password: deletePwd, conferma: deleteConferma } });
      const casc = r.data?.cascade || {};
      const totalRemoved = Object.values(casc).filter(v => typeof v === "number").reduce((a, b) => a + b, 0);
      clearToken();
      toast.success(`Account eliminato definitivamente (${totalRemoved} record rimossi).`);
      window.location.href = "/";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setDeletePwd("");
    setDeleteConferma("");
    setDeleteModalOpen(true);
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const r = await api.post("/notifications/test");
      toast.success(`Email di test inviata a ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally { setTestingEmail(false); }
  };

  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const sendTestWhatsapp = async () => {
    setTestingWhatsapp(true);
    try {
      const r = await api.post("/notifications/test-whatsapp");
      toast.success(`Messaggio WhatsApp inviato a ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally { setTestingWhatsapp(false); }
  };

  const [reseeding, setReseeding] = useState(false);
  const reseedScadenze = async () => {
    setReseeding(true);
    try {
      const r = await api.post("/deadlines/seed-standard?force=true");
      toast.success(`Rigenerate ${r.data.seeded} scadenze in base al tuo profilo attuale`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rigenerazione");
    } finally { setReseeding(false); }
  };

  // Global completeness (excluding email_notifications which is boolean)
  const totalFields = Object.values(SECTIONS).flat().length;
  const filledFields = Object.values(SECTIONS).flat().filter((k) => form[k] !== "" && form[k] !== null).length;
  const completionPct = Math.round((filledFields / totalFields) * 100);

  const radarActive = form.regione && form.settore;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header + completeness */}
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Impostazioni</div>
        <h1 className="font-heading font-bold text-4xl tracking-tighter">Il tuo profilo.</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Più completo è il profilo, più preciso è il Radar Bandi e più campi TUOTAX può compilare per te
          quando presenti una domanda o generi un documento.
        </p>
      </div>

      {/* Completion bar */}
      <Card className="p-5 rounded-2xl border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold">Completamento profilo</div>
            <div className="text-xs text-muted-foreground">{filledFields} di {totalFields} campi compilati</div>
          </div>
          <div className="text-2xl font-heading font-bold text-primary" data-testid="profile-completion">{completionPct}%</div>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${completionPct}%` }} />
        </div>
      </Card>

      {!radarActive && (
        <Card className="p-4 rounded-2xl border-2 border-secondary/30 bg-secondary/5 flex gap-3 items-start" data-testid="settings-incomplete-alert">
          <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Radar Bandi disattivato.</strong> Compila almeno <em>Regione</em> (sezione Sede) e <em>Settore</em> (sezione Attività) per riceverne di rilevanti.
          </div>
        </Card>
      )}

      {/* Accordion sections */}
      <Accordion type="multiple" defaultValue={["personal"]} className="space-y-3">

        {/* Dati personali */}
        <AccordionItem value="personal" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={UserIcon}
              title="Anagrafica personale"
              subtitle="Nome, codice fiscale, dati di nascita"
              filledCount={countFilled(form, SECTIONS.personal)}
              totalCount={SECTIONS.personal.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome e cognome" value={form.full_name} onChange={set("full_name")} placeholder="Mario Rossi" testid="settings-name" className="md:col-span-2" />
              <Field label="Codice fiscale" value={form.codice_fiscale} onChange={(v) => set("codice_fiscale")(v.toUpperCase())} placeholder="RSSMRA85M01H501U" testid="settings-cf" hint="16 caratteri" />
              <Field label="Email (non modificabile)" value={user?.email || ""} onChange={() => {}} disabled testid="settings-email" />
              <Field label="Data di nascita" type="date" value={form.data_nascita} onChange={set("data_nascita")} testid="settings-data-nascita" />
              <Field label="Luogo di nascita" value={form.luogo_nascita} onChange={set("luogo_nascita")} placeholder="Città (Provincia)" testid="settings-luogo-nascita" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Contatti */}
        <AccordionItem value="contatti" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={Phone}
              title="Contatti"
              subtitle="Telefono, PEC, sito web"
              filledCount={countFilled(form, SECTIONS.contatti)}
              totalCount={SECTIONS.contatti.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Telefono" value={form.telefono} onChange={set("telefono")} placeholder="+39 333 1234567" testid="settings-telefono" />
              <Field label="PEC (Posta Elettronica Certificata)" value={form.pec} onChange={set("pec")} placeholder="mario.rossi@pec.it" testid="settings-pec" hint="Obbligatoria per la maggior parte dei bandi" />
              <Field label="Sito web" value={form.sito_web} onChange={set("sito_web")} placeholder="https://miosito.it" testid="settings-sito" className="md:col-span-2" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Sede legale */}
        <AccordionItem value="sede" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={MapPin}
              title="Sede legale"
              subtitle="Indirizzo, città, regione — dove ha sede la tua attività"
              filledCount={countFilled(form, SECTIONS.sede)}
              totalCount={SECTIONS.sede.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Indirizzo" value={form.indirizzo} onChange={set("indirizzo")} placeholder="Via Roma 12" testid="settings-indirizzo" className="md:col-span-2" />
              <Field label="CAP" value={form.cap} onChange={set("cap")} placeholder="20121" testid="settings-cap" />
              <Field label="Città" value={form.citta} onChange={set("citta")} placeholder="Milano" testid="settings-citta" />
              <Field label="Provincia (sigla)" value={form.provincia} onChange={(v) => set("provincia")(v.toUpperCase().slice(0, 2))} placeholder="MI" testid="settings-provincia" />
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Regione</Label>
                <Select value={form.regione} onValueChange={set("regione")}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-regione"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{REGIONI.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Attività fiscale */}
        <AccordionItem value="attivita" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={Briefcase}
              title="Attività e P.IVA"
              subtitle="Ragione sociale, P.IVA, ATECO, regime fiscale"
              filledCount={countFilled(form, SECTIONS.attivita)}
              totalCount={SECTIONS.attivita.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Ragione sociale / Denominazione" value={form.ragione_sociale} onChange={set("ragione_sociale")} placeholder="Es. Rossi Consulting S.r.l. o Mario Rossi (se persona fisica)" testid="settings-ragione-sociale" className="md:col-span-2" />
              <Field label="Partita IVA (numero)" value={form.partita_iva} onChange={(v) => set("partita_iva")(v.replace(/\D/g, "").slice(0, 11))} placeholder="12345678901" testid="settings-piva-num" hint="11 cifre" />
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Tipo P.IVA / Regime</Label>
                <Select value={form.tipo_piva} onValueChange={set("tipo_piva")}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-tipo-piva"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_PIVA.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Codice ATECO principale</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={form.codice_ateco ?? ""}
                    onChange={(e) => set("codice_ateco")(e.target.value)}
                    placeholder="62.02.00"
                    className="h-11 rounded-xl flex-1"
                    data-testid="settings-ateco"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAtecoAiOpen(true)}
                    className="h-11 rounded-xl flex-shrink-0 border-primary/30 text-primary hover:bg-primary/5 px-3"
                    data-testid="settings-ateco-ai-btn"
                    title="Non conosci il tuo codice ATECO? Chiedi all'AI"
                  >
                    <Sparkles className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Aiutante AI</span>
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Lo trovi sulla visura camerale o portale AdE</p>
                {form.codice_ateco && atecoInfo && (
                  atecoInfo.coefficiente ? (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-primary/5 border border-primary/15 p-2.5" data-testid="settings-ateco-hint">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="text-xs">
                        <span className="text-foreground">Coefficiente forfettario: <strong>{Math.round(atecoInfo.coefficiente * 100)}%</strong></span>
                        <div className="text-muted-foreground mt-0.5">{atecoInfo.gruppo}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-500/5 border border-amber-500/20 p-2.5" data-testid="settings-ateco-hint">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-muted-foreground">
                        Codice ATECO non riconosciuto per il regime forfettario. Verifica sul portale Agenzia Entrate.
                      </div>
                    </div>
                  )
                )}
              </div>
              <Field label="Codici ATECO secondari" value={form.codici_ateco_secondari} onChange={set("codici_ateco_secondari")} placeholder="63.11.30, 82.30.00" testid="settings-ateco-sec" hint="Separati da virgola" className="md:col-span-2" />
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Settore</Label>
                <Select value={form.settore} onValueChange={set("settore")}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-settore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{SETTORI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Dimensione</Label>
                <Select value={form.dimensione} onValueChange={set("dimensione")}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-dimensione"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIMENSIONI.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Data inizio attività" type="date" value={form.data_inizio_attivita} onChange={set("data_inizio_attivita")} testid="settings-inizio-attivita" />
              <Field label="Fatturato ultimo anno (€)" type="number" value={form.fatturato_ultimo_anno} onChange={set("fatturato_ultimo_anno")} placeholder="Es. 42000" testid="settings-fatturato" hint="Solo cifra, senza simboli" className="md:col-span-2" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Camera di Commercio & Albi */}
        <AccordionItem value="cciaa" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={Award}
              title="Camera di Commercio e Albi professionali"
              subtitle="REA, CCIAA, iscrizione ad albo"
              filledCount={countFilled(form, SECTIONS.cciaa)}
              totalCount={SECTIONS.cciaa.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Numero REA" value={form.rea_numero} onChange={set("rea_numero")} placeholder="MI-1234567" testid="settings-rea" hint="Repertorio Economico Amministrativo" />
              <Field label="CCIAA (Camera di Commercio)" value={form.rea_cciaa} onChange={set("rea_cciaa")} placeholder="Milano" testid="settings-cciaa" />
              <Field label="Data iscrizione REA" type="date" value={form.rea_data_iscrizione} onChange={set("rea_data_iscrizione")} testid="settings-rea-data" className="md:col-span-2" />
              <Field label="Nome albo professionale" value={form.albo_nome} onChange={set("albo_nome")} placeholder="Es. Albo Ingegneri" testid="settings-albo-nome" />
              <Field label="Numero iscrizione albo" value={form.albo_numero} onChange={set("albo_numero")} placeholder="12345" testid="settings-albo-num" />
              <Field label="Data iscrizione albo" type="date" value={form.albo_data_iscrizione} onChange={set("albo_data_iscrizione")} testid="settings-albo-data" className="md:col-span-2" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Casse & Enti */}
        <AccordionItem value="casse" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={Landmark}
              title="Casse previdenziali ed enti"
              subtitle="Cassa professionale, INPS, INAIL"
              filledCount={countFilled(form, SECTIONS.casse)}
              totalCount={SECTIONS.casse.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-muted-foreground">Cassa previdenziale</Label>
                <Select value={form.cassa_previdenziale || "none"} onValueChange={(v) => set("cassa_previdenziale")(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-cassa"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{CASSE.map((c) => <SelectItem key={c.v || "none"} value={c.v || "none"}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Numero iscrizione cassa" value={form.cassa_numero} onChange={set("cassa_numero")} placeholder="123456" testid="settings-cassa-num" />
              <Field label="INPS - Matricola" value={form.inps_matricola} onChange={set("inps_matricola")} placeholder="1234567890" testid="settings-inps" hint="Per dipendenti / imprenditori" />
              <Field label="INAIL - PAT" value={form.inail_pat} onChange={set("inail_pat")} placeholder="1234567890" testid="settings-inail" hint="Posizione Assicurativa Territoriale" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Bancari & Fatturazione */}
        <AccordionItem value="bancari" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <SectionHeader
              Icon={Wallet}
              title="Bancari e fatturazione elettronica"
              subtitle="IBAN, codice SDI, PEC fatturazione"
              filledCount={countFilled(form, SECTIONS.bancari)}
              totalCount={SECTIONS.bancari.length}
            />
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-6 pt-2">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="IBAN" value={form.iban} onChange={(v) => set("iban")(v.toUpperCase().replace(/\s/g, ""))} placeholder="IT60X0542811101000000123456" testid="settings-iban" className="md:col-span-2" />
              <Field label="Nome banca" value={form.banca_nome} onChange={set("banca_nome")} placeholder="Es. Intesa Sanpaolo" testid="settings-banca" />
              <Field label="Codice SDI (destinatario)" value={form.codice_sdi} onChange={(v) => set("codice_sdi")(v.toUpperCase().slice(0, 7))} placeholder="0000000" testid="settings-sdi" hint="7 caratteri per FatturaPA" />
              <Field label="PEC per fatturazione elettronica" value={form.pec_fatturazione} onChange={set("pec_fatturazione")} placeholder="fatture@pec.it" testid="settings-pec-fat" hint="Se diversa dalla PEC principale" className="md:col-span-2" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Dipendente / famiglia */}
        <AccordionItem value="dipendente" className="border rounded-2xl bg-card overflow-hidden">
          <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid="acc-dipendente">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><UserIcon className="w-5 h-5 text-primary" /></div>
              <div className="text-left">
                <div className="font-heading font-semibold">Lavoro dipendente e famiglia</div>
                <div className="text-xs text-muted-foreground">RAL, figli a carico, ISEE — usati per Assegno Unico, bonus, calcolo netto busta paga</div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="flex items-center justify-between rounded-xl bg-muted/50 p-4 mb-4">
              <div>
                <Label className="text-sm font-semibold">Percepisci reddito da lavoro dipendente?</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Se sì, ti sblocchiamo i calcolatori busta paga e bonus dipendenti.</p>
              </div>
              <Switch checked={!!form.has_dipendente_income} onCheckedChange={(v) => setForm((f) => ({ ...f, has_dipendente_income: v }))} data-testid="settings-has-dipendente" />
            </div>

            {form.has_dipendente_income && (
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="RAL — Reddito Annuo Lordo (€)" value={form.ral_lordo} onChange={set("ral_lordo")} type="number" placeholder="Es. 30000" testid="settings-ral" hint="Lordo annuo prima di tasse e contributi" />
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Tipo contratto</Label>
                  <Select value={form.tipo_contratto || "indeterminato"} onValueChange={set("tipo_contratto")}>
                    <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-contratto"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indeterminato">Indeterminato</SelectItem>
                      <SelectItem value="determinato">Determinato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground">Mensilità</Label>
                  <Select value={String(form.mensilita || 13)} onValueChange={(v) => setForm((f) => ({ ...f, mensilita: Number(v) }))}>
                    <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="settings-mens"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="12">12</SelectItem><SelectItem value="13">13</SelectItem><SelectItem value="14">14</SelectItem></SelectContent>
                  </Select>
                </div>
                <Field label="Aliquota addizionale REGIONALE (opzionale, es. 0.0173)" value={form.aliquota_add_regionale} onChange={set("aliquota_add_regionale")} type="number" placeholder="Media 0.0173" testid="settings-add-reg" hint="Se lasci vuoto usiamo la media 1,73%" />
                <Field label="Aliquota addizionale COMUNALE (opzionale, es. 0.008)" value={form.aliquota_add_comunale} onChange={set("aliquota_add_comunale")} type="number" placeholder="Media 0.0070" testid="settings-add-com" hint="Se lasci vuoto usiamo la media 0,70%" />
              </div>
            )}

            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-semibold">Figli a carico (età)</Label>
                <p className="text-xs text-muted-foreground mb-2">Servono per calcolare l'Assegno Unico e i bonus famiglia.</p>
                {(form.figli_a_carico || []).map((f, i) => (
                  <div key={i} className="flex gap-2 mb-2 items-end">
                    <div className="flex-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Età (anni)</Label>
                      <Input type="number" min={0} max={24} value={f.eta ?? 0} onChange={(e) => {
                        const arr = [...form.figli_a_carico];
                        arr[i] = { ...arr[i], eta: Number(e.target.value) };
                        setForm((s) => ({ ...s, figli_a_carico: arr }));
                      }} className="h-11 rounded-xl mt-1" data-testid={`settings-figlio-${i}-eta`} />
                    </div>
                    <div className="flex-[1.5]">
                      <Label className="text-[10px] uppercase text-muted-foreground">Disabilità</Label>
                      <Select value={f.disabilita || "no"} onValueChange={(v) => {
                        const arr = [...form.figli_a_carico];
                        arr[i] = { ...arr[i], disabilita: v === "no" ? null : v };
                        setForm((s) => ({ ...s, figli_a_carico: arr }));
                      }}>
                        <SelectTrigger className="h-11 rounded-xl mt-1" data-testid={`settings-figlio-${i}-disab`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">Nessuna</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="grave">Grave</SelectItem>
                          <SelectItem value="non_autosufficiente">Non autosufficiente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => {
                      const arr = form.figli_a_carico.filter((_, idx) => idx !== i);
                      setForm((s) => ({ ...s, figli_a_carico: arr }));
                    }} className="h-11 w-11" data-testid={`settings-figlio-${i}-remove`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => {
                  const arr = [...(form.figli_a_carico || []), { eta: 0, disabilita: null }];
                  setForm((s) => ({ ...s, figli_a_carico: arr }));
                }} data-testid="settings-add-figlio">+ Aggiungi figlio</Button>
              </div>
              <Field label="ISEE (opzionale)" value={form.isee} onChange={set("isee")} type="number" placeholder="Es. 22000" testid="settings-isee" hint="Serve per Assegno Unico, bonus asilo e bonus energia" />
              <div className="grid md:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.coniuge_a_carico} onChange={(e) => setForm((f) => ({ ...f, coniuge_a_carico: e.target.checked }))} className="w-4 h-4" data-testid="settings-coniuge" />
                  Coniuge a carico
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.madre_under_21} onChange={(e) => setForm((f) => ({ ...f, madre_under_21: e.target.checked }))} className="w-4 h-4" data-testid="settings-madre-u21" />
                  Madre &lt; 21 anni
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.entrambi_genitori_lavoratori} onChange={(e) => setForm((f) => ({ ...f, entrambi_genitori_lavoratori: e.target.checked }))} className="w-4 h-4" data-testid="settings-entrambi-lav" />
                  Entrambi genitori lavoratori
                </label>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Notifiche */}
      <Card className="p-6 rounded-2xl border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Bell className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Notifiche</h2>
            <p className="text-xs text-muted-foreground">Scegli quali avvisi ricevere e su quale canale.</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div className="flex-1">
              <div className="font-medium text-sm">Email — Master switch</div>
              <div className="text-xs text-muted-foreground">Se lo spegni, nessuna email arriva (nemmeno reset password).</div>
            </div>
            <Switch checked={form.email_notifications} onCheckedChange={set("email_notifications")} data-testid="settings-email-notif" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div className="flex-1">
              <div className="font-medium text-sm">📅 Promemoria scadenze fiscali</div>
              <div className="text-xs text-muted-foreground">Reminder email a 30, 7 e 1 giorno prima di F24, IRPEF, IVA, INPS.</div>
            </div>
            <Switch checked={!!form.email_deadlines_alerts} onCheckedChange={set("email_deadlines_alerts")} data-testid="settings-email-deadlines" />
          </div>
          <div className="flex items-center justify-between rounded-xl border p-4">
            <div className="flex-1">
              <div className="font-medium text-sm">🎯 Radar Bandi</div>
              <div className="text-xs text-muted-foreground">Digest nuovi bandi rilevanti (max 1 email/giorno).</div>
            </div>
            <Switch checked={!!form.email_bandi_alerts} onCheckedChange={set("email_bandi_alerts")} data-testid="settings-email-bandi" />
          </div>

          <div className="pt-2 border-t">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 mt-2">WhatsApp</div>
            <Field label="Cellulare (per WhatsApp)" value={form.phone_number} onChange={set("phone_number")} placeholder="+39 333 1234567" testid="settings-phone" hint="Formato E.164, es. +393331234567. In Beta usiamo la Sandbox Twilio: dovrai inviare 'join <codice>' al numero +1 415 523 8886 da WhatsApp per attivare la ricezione." />
            <div className="flex items-center justify-between rounded-xl border p-4 mt-3 bg-muted/30">
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">💬 Notifiche WhatsApp <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/20 text-secondary font-bold uppercase">Beta</span></div>
                <div className="text-xs text-muted-foreground">Promemoria scadenze via WhatsApp a 30/7/1 giorni dalla data.</div>
              </div>
              <Switch checked={!!form.whatsapp_alerts} onCheckedChange={set("whatsapp_alerts")} disabled={!form.phone_number} data-testid="settings-whatsapp" />
            </div>
          </div>

          <div className="flex gap-2 mt-2 flex-wrap">
            <Button variant="outline" onClick={sendTestEmail} disabled={testingEmail} className="rounded-xl h-11" data-testid="settings-test-email">
              <Send className="w-4 h-4 mr-2" /> {testingEmail ? "Invio..." : "Invia email di prova"}
            </Button>
            <Button variant="outline" onClick={sendTestWhatsapp} disabled={testingWhatsapp || !form.phone_number} className="rounded-xl h-11" data-testid="settings-test-whatsapp">
              <Send className="w-4 h-4 mr-2" /> {testingWhatsapp ? "Invio..." : "Invia WhatsApp di prova"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Scadenze fiscali auto-generate */}
      <Card className="p-6 rounded-2xl border" data-testid="settings-scadenze-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><CalendarDays className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Scadenze fiscali automatiche</h2>
            <p className="text-xs text-muted-foreground">Ricostruite in base al tuo profilo (ATECO, tipo P.IVA, cassa).</p>
          </div>
        </div>
        <div className="rounded-xl bg-muted/30 p-4 text-sm space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Regime previdenziale rilevato:</span>
            <span className="font-bold text-primary" data-testid="settings-regime-label">
              {form.cassa_previdenziale ? `Cassa (${form.cassa_previdenziale})` :
                (form.codice_ateco || "").match(/^(4[1-3]|1[0-9]|2[0-9]|3[0-3]|95)/) ? "INPS Artigiani" :
                (form.codice_ateco || "").match(/^(4[5-7]|55|56|77|79)/) ? "INPS Commercianti" :
                form.tipo_piva === "forfettario" || form.tipo_piva === "ordinario" ? "INPS Gestione Separata" :
                "Nessuno (non P.IVA)"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Se sei in Gestione Separata i contributi si versano insieme al saldo/acconti IRPEF a giugno e novembre (nessuna rata fissa separata).
            Se sei artigiano o commerciante, hai 4 rate fisse trimestrali. Se hai una cassa (Inarcassa, Cassa Forense…) TUOTAX non genera scadenze INPS.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={reseedScadenze}
          disabled={reseeding}
          className="rounded-xl h-11 mt-3"
          data-testid="settings-reseed-scadenze"
        >
          {reseeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Rigenera scadenze fiscali
        </Button>
        <p className="text-[11px] text-muted-foreground mt-2">
          Rimuove le scadenze auto-generate non ancora spuntate e le ricrea in base al profilo attuale. Le scadenze aggiunte manualmente e quelle già completate NON vengono toccate.
        </p>
      </Card>

      {/* Sticky save */}
      <div className="sticky bottom-6 flex justify-end z-10">
        <Button onClick={save} disabled={saving} className="rounded-full h-12 px-8 shadow-2xl" data-testid="settings-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva modifiche"}
        </Button>
      </div>

      {/* GDPR */}
      <Card className="p-6 rounded-2xl border mt-8" data-testid="gdpr-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center"><Shield className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Privacy e dati (GDPR)</h2>
            <p className="text-xs text-muted-foreground">I tuoi dati sono tuoi. Sempre.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Button variant="outline" onClick={exportData} className="rounded-xl h-12 justify-start" data-testid="gdpr-export">
            <Download className="w-4 h-4 mr-2" /> Esporta i miei dati (JSON)
          </Button>
          <Button variant="outline" onClick={openDeleteModal} className="rounded-xl h-12 justify-start border-destructive/40 text-destructive hover:bg-destructive/5" data-testid="gdpr-delete">
            <Trash2 className="w-4 h-4 mr-2" /> Elimina account
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          La cancellazione è immediata e irreversibile. Consulta la <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary font-semibold underline">privacy policy</a>.
        </p>
      </Card>

      {/* Modal Aiutante AI ATECO */}
      <Dialog open={atecoAiOpen} onOpenChange={setAtecoAiOpen}>
        <DialogContent className="max-w-2xl rounded-2xl" data-testid="ateco-ai-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="font-heading text-xl">Aiutante AI — Codice ATECO</DialogTitle>
                <DialogDescription className="text-xs">
                  Descrivi la tua attività e l'AI ti suggerirà i codici ATECO 2007 più adatti con il coefficiente forfettario.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-muted-foreground">Cosa fai? (in parole semplici)</Label>
              <Textarea
                value={atecoAiDesc}
                onChange={(e) => setAtecoAiDesc(e.target.value)}
                placeholder="Es. 'Sviluppo siti web e app mobile per piccole imprese' oppure 'Fotografo di matrimoni ed eventi' oppure 'Idraulico che fa impianti e riparazioni'"
                rows={3}
                maxLength={500}
                className="rounded-xl mt-1.5 resize-none"
                data-testid="ateco-ai-desc"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-muted-foreground">Più dettagli dai, più preciso sarà il suggerimento.</p>
                <span className="text-xs text-muted-foreground">{atecoAiDesc.length}/500</span>
              </div>
            </div>

            <Button
              onClick={askAtecoAI}
              disabled={atecoAiLoading || atecoAiDesc.trim().length < 5}
              className="w-full rounded-xl h-11"
              data-testid="ateco-ai-submit"
            >
              {atecoAiLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cerco i codici migliori...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Trova codici ATECO</>
              )}
            </Button>

            {atecoAiResults.length > 0 && (
              <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="ateco-ai-results">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground pt-2">Suggerimenti ({atecoAiResults.length})</p>
                {atecoAiResults.map((s, idx) => (
                  <button
                    key={s.codice + idx}
                    type="button"
                    onClick={() => applyAtecoSuggestion(s.codice)}
                    className="w-full text-left rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 p-3.5 transition-colors group"
                    data-testid={`ateco-ai-result-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-primary text-base">{s.codice}</span>
                        {s.coefficiente != null ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                            {Math.round(s.coefficiente * 100)}%
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 font-medium">
                            non validato
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        Usa questo →
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground mb-1">{s.descrizione}</p>
                    {s.motivo && (
                      <p className="text-xs text-muted-foreground italic">💡 {s.motivo}</p>
                    )}
                  </button>
                ))}
                <p className="text-xs text-muted-foreground pt-2 pb-1">
                  ⚠️ I suggerimenti sono generati da AI: verifica sempre col commercialista prima di dichiarare il codice ATECO ufficiale.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Elimina account - conferma con password + digitazione ELIMINA */}
      <Dialog open={deleteModalOpen} onOpenChange={(v) => !deleting && setDeleteModalOpen(v)}>
        <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-destructive flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Elimina definitivamente il tuo account
            </DialogTitle>
            <DialogDescription>
              Questa azione è <strong>irreversibile</strong>. Non potremo recuperare i tuoi dati.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl bg-destructive/5 border border-destructive/20 p-4 space-y-2">
              <div className="text-sm font-semibold text-destructive">Cosa verrà eliminato in modo permanente:</div>
              <ul className="text-xs text-foreground/80 space-y-1 list-disc list-inside">
                <li>Il tuo profilo utente e le credenziali</li>
                <li>Tutti i documenti caricati (buste paga, fatture XML, ricevute)</li>
                <li>Tutti i movimenti bancari importati</li>
                <li>Tutte le scadenze e gli eventi fiscali (mutui, sanitarie, ecc.)</li>
                <li>I piani fiscali e le simulazioni salvate</li>
                <li>Il radar bandi personalizzato</li>
                <li>Le segnalazioni inviate e i relativi screenshot</li>
                <li>Le sessioni del copilota AI e i messaggi</li>
              </ul>
              <div className="text-[11px] text-muted-foreground pt-1">
                Suggerimento: se vuoi tenerti una copia, chiudi questa finestra e usa prima "Esporta i miei dati (JSON)".
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">Conferma la tua password</Label>
              <Input
                type="password"
                value={deletePwd}
                onChange={(e) => setDeletePwd(e.target.value)}
                placeholder="La password del tuo account"
                className="h-11 rounded-xl mt-1"
                data-testid="delete-account-password"
                autoComplete="current-password"
                disabled={deleting}
              />
            </div>

            <div>
              <Label className="text-xs font-medium">Per confermare, digita <strong className="text-destructive font-mono">ELIMINA</strong></Label>
              <Input
                value={deleteConferma}
                onChange={(e) => setDeleteConferma(e.target.value)}
                placeholder="ELIMINA"
                className="h-11 rounded-xl mt-1 font-mono"
                data-testid="delete-account-conferma"
                disabled={deleting}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deleting} data-testid="delete-account-cancel">
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={doDeleteAccount}
              disabled={deleting || !deletePwd || deleteConferma.toUpperCase() !== "ELIMINA"}
              data-testid="delete-account-confirm"
            >
              {deleting ? "Eliminazione in corso…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
